import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { GridsterConfig, GridType } from 'angular-gridster2';

import { WidgetService } from '../../main/widgets/widget.service';
import { Widget, WidgetGroup } from '../../shared/models/widget';
import { debounceTime, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as _ from 'lodash-es';
import { environment } from 'environments/environment';

@Component({
  selector: 'layout-builder',
  templateUrl: './layout-builder.component.html',
  styleUrls: ['./layout-builder.component.scss'],
})
export class LayoutBuilderComponent implements OnInit, OnDestroy, OnChanges {
  // backing variables
  _options: GridsterConfig;
  _layout: Widget[] = []; // widgets in layout
  _locked = true;
  _height = '';
  _showToolbar = true;
  debouncer: Subject<any> = new Subject();
  defaultHeight = 'calc(100% - 64px)';

  handleMetaChanges = {
    metadataChanged: (data) => {
      const updatedWidget = this._layout.find((x) => x.id === data.id);
      if (updatedWidget) {
        updatedWidget.metadata = data;
        this.saveLayout();
      }
    },
  };

  @Input() set layout(val: Widget[]) {
    this._layout = [...val];
  }

  get layout() {
    return this._layout;
  }

  @Input() widgets: WidgetGroup[][] = [];
  @Input() toolbarColour: string = '#0277bd';

  ngOnChanges(changes): void {
    if (changes.widgets && changes.widgets.currentValue) {
      this.widgets = changes.widgets.currentValue;
    }
  }

  @Input() set options(val: GridsterConfig) {
    this.setOptions(val);
  }

  @Input() set showToolbar(val: boolean) {
    this.defaultHeight = !val ? '100%' : 'calc(100% - 64px)';
    this._showToolbar = val;
  }

  @Input() set height(val: number) {
    this._height = val.toString() + 'px';
  }

  @Input() set locked(val: boolean) {
    this._locked = val;
    if (this._options) {
      this._options.draggable.enabled = !val;
      this._options.resizable.enabled = !val;
      this._options.api.optionsChanged();
    }
  }

  @Input() border = false;

  get locked() {
    return this._locked;
  }

  @Input() contentBackgroundColor;

  @Output() onAddWidget: EventEmitter<any> = new EventEmitter();
  @Output() onRemoveWidget: EventEmitter<any> = new EventEmitter();
  @Output() onLayoutChanged: EventEmitter<any> = new EventEmitter();
  @Output() onDebouncedLayoutChanged: EventEmitter<any> = new EventEmitter();

  constructor(private widgetService: WidgetService) {}

  ngOnInit() {
    if (!this._options) {
      this.setOptions({});
    }

    // debouncer used to debounce layout changes - this should mean that the event isn't repeatedly fired as changes are made
    this.debouncer.pipe(debounceTime(3000)).subscribe((val) => {
      this.onDebouncedLayoutChanged.emit(val);
    });
  }

  ngOnDestroy(): void {
    if (this.debouncer) this.debouncer.unsubscribe();
  }

  // set gridster options
  setOptions(options: GridsterConfig) {
    this._options = {
      gridType: options.gridType || GridType.Fixed,
      setGridSize: options.setGridSize || false,
      fixedColWidth: environment.enableGridSizing
        ? options.fixedColWidth || 35
        : options.fixedColWidth || 70,
      fixedRowHeight: environment.enableGridSizing
        ? options.fixedColWidth || 35
        : options.fixedColWidth || 70,
      margin: options.margin || 0,
      minCols: options.minCols || 1,
      maxCols: environment.enableGridSizing
        ? options.fixedColWidth || 100
        : options.fixedColWidth || 50,
      minRows: options.minRows || 1,
      maxRows: environment.enableGridSizing
        ? options.maxRows || 200
        : options.maxRows || 100,
      minItemCols: options.minItemCols || 2,
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
      itemChangeCallback: () => this.saveLayout(),
    };
  }

  addWidget(widgetGroup: WidgetGroup) {
    // console.log('Add widgets:', widgetGroup, widgetGroup.widgets);
    const widgets = [...widgetGroup.widgets];
    widgets.forEach((item) => {
      // set the correct widget based on type
      const type = this.widgetService.getWidgetType(item.type);
      item.widget = this.widgetService.getWidget(type);

      // assign new id if necessary
      if (this._layout.find((w) => w.id === item.id)) {
        item.id = this._layout.length + 1;
      }

      if (item.configurable) {
        this.displayWidgetSettings(widgetGroup, item);
      }

      // console.log('Adding item: ', item, this._layout);

      // add the completed widget to the layout
      this._layout.push(item);

      // make sure changes are notified
      this.onAddWidget.emit(item);
    });
    if (widgets.length > 0) {
      this.saveLayout();
    }
  }

  changedOptions() {
    if (this._options) {
      this._options.api.optionsChanged();
    }
  }

  removeWidget($event, item) {
    // $event.preventDefault();
    // $event.stopPropagation();
    const idx = this._layout.indexOf(item);
    if (idx > -1) {
      this._layout.splice(idx, 1);
      this.onRemoveWidget.emit(item);
      this.saveLayout();
    }
  }

  displayWidgetSettings($event, item) {
    this.widgetService
      .displaySettings(item)
      .pipe(take(1))
      .subscribe((updatedWidget) => {
        if (updatedWidget) {
          if (updatedWidget.metadata.settings) {
            // not sure how best to action the refresh, the following seems to work...
            const idx = this._layout.indexOf(updatedWidget);
            this._layout.splice(idx, 1, _.cloneDeep(updatedWidget));
            setTimeout(() => {
              this.saveLayout();
            });
          } else {
            this.removeWidget($event, updatedWidget);
          }
        }
      });
  }

  saveLayout() {
    this.onLayoutChanged.emit(this._layout);
    this.debouncer.next(this._layout);
  }

  toggleLock(state: boolean) {
    this._locked = !state;
    if (this._options) {
      this._options.draggable.enabled = state;
      this._options.resizable.enabled = state;
      this._options.api.optionsChanged();
    }
  }

  public resize() {
    // Used if tabs cause grid to load as wrong dimensions
    if (this._options) {
      this._options.api.resize();
    }
  }

  // trackByFn(index, item){
  //     return item.id;
  // }
}
